import React from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isSameDay, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AssignedChore, ViewMode, FilterMode } from '../types';

interface CalendarProps {
  currentDate: Date;
  assignedChores: AssignedChore[];
  viewMode: ViewMode;
  filterMode: FilterMode;
  onDateChange: (date: Date) => void;
  onViewModeChange: (mode: ViewMode) => void;
  onFilterChange: (filter: FilterMode) => void;
  onToggleChore: (choreId: string) => void;
}

export const Calendar: React.FC<CalendarProps> = ({
  currentDate,
  assignedChores,
  viewMode,
  filterMode,
  onDateChange,
  onViewModeChange,
  onFilterChange,
  onToggleChore
}) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const filteredChores = assignedChores.filter(chore => {
    if (filterMode === 'all') return true;
    return chore.assignedTo.toLowerCase() === filterMode;
  });

  const getChoresForDay = (date: Date) => {
    return filteredChores.filter(chore => 
      isSameDay(parseISO(chore.date), date)
    );
  };

  const getDayClass = (date: Date) => {
    const isToday = isSameDay(date, new Date());
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    return `calendar-day p-2 border border-gray-200 ${
      isToday ? 'bg-blue-50 border-blue-300' : ''
    } ${isWeekend ? 'bg-gray-50' : 'bg-white'}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => onDateChange(subWeeks(currentDate, 1))}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Previous Week"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          
          <button
            onClick={() => onDateChange(addWeeks(currentDate, 1))}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Next Week"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onViewModeChange('week')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'week' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => onViewModeChange('month')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'month' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Month
            </button>
          </div>

          {/* Filter Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onFilterChange('all')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                filterMode === 'all' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All
            </button>
            <button
              onClick={() => onFilterChange('mary')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                filterMode === 'mary' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mary
            </button>
            <button
              onClick={() => onFilterChange('maxwell')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                filterMode === 'maxwell' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Maxwell
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid (desktop/tablet) */}
      <div className="hidden md:grid grid-cols-7 gap-1">
        {/* Day Headers */}
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <div key={day} className="p-2 text-center font-semibold text-gray-600 bg-gray-50 rounded-t-lg">
            {day}
          </div>
        ))}

        {/* Calendar Days */}
        {weekDays.map((day, index) => {
          const dayChores = getChoresForDay(day);
          const isToday = isSameDay(day, new Date());
          
          return (
            <div key={index} className={getDayClass(day)}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${
                  isToday ? 'text-blue-600' : 'text-gray-900'
                }`}>
                  {format(day, 'd')}
                </span>
                {isToday && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                )}
              </div>
              
              <div className="space-y-1">
                {dayChores.map(chore => (
                  <div
                    key={chore.id}
                    className={`chore-item p-2 rounded text-xs cursor-pointer transition-all ${
                      chore.completed 
                        ? 'bg-green-100 text-green-800 line-through' 
                        : chore.assignedTo === 'Mary'
                        ? 'bg-pink-100 text-pink-800 hover:bg-pink-200'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                    onClick={() => onToggleChore(chore.id)}
                  >
                    <div className="font-medium truncate">{chore.task}</div>
                    <div className="flex items-center justify-between text-xs opacity-75">
                      <span>{chore.assignedTo}</span>
                      <span>{chore.timeMinutes}m</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: horizontal scroll of days */}
      <div className="md:hidden -mx-4 px-4 overflow-x-auto">
        <div className="flex gap-3 pb-2">
          {weekDays.map((day, index) => {
            const dayChores = getChoresForDay(day);
            const isToday = isSameDay(day, new Date());
            return (
              <div key={index} className="flex-shrink-0 w-[80vw] max-w-xs border border-gray-200 rounded-lg bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-700">{format(day, 'EEE')}</div>
                    <div className={`text-lg font-bold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>{format(day, 'MMM d')}</div>
                  </div>
                  {isToday && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                </div>

                <div className="space-y-2">
                  {dayChores.length === 0 && (
                    <div className="text-xs text-gray-500">No chores</div>
                  )}

                  {dayChores.map(chore => (
                    <button
                      key={chore.id}
                      className={`w-full text-left chore-item p-2 rounded text-xs transition-all ${
                        chore.completed 
                          ? 'bg-green-100 text-green-800 line-through' 
                          : chore.assignedTo === 'Mary'
                          ? 'bg-pink-100 text-pink-800 active:bg-pink-200'
                          : 'bg-blue-100 text-blue-800 active:bg-blue-200'
                      }`}
                      onClick={() => onToggleChore(chore.id)}
                    >
                      <div className="font-medium truncate">{chore.task}</div>
                      <div className="flex items-center justify-between text-xs opacity-75">
                        <span>{chore.assignedTo}</span>
                        <span>{chore.timeMinutes}m</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}; 